/**
 * Table state and row interaction handlers for the expandable order-auth table.
 */
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { RootState } from "../../../redux/store/store";
import { generateExpandableTableForOrderAuthData } from "../singleTableRow";
import {
  ExpandableTableForOrderAuthTableRow,
  SingleOrderClubbedData,
} from "../types";

/** Hook return value for expandable order-auth table state and handlers. */
export interface UseExpandableOrderAuthTableResult {
  /** CustomTable row objects including expand/checkbox UI state. */
  tableData: ExpandableTableForOrderAuthTableRow[];
  /** Expands or collapses a row by chevron; only one row stays expanded. */
  handleRowExpansionChange: (expanded: boolean, id: string) => void;
  /**
   * Toggles multi-select for a row checkbox.
   * Checking also auto-expands that row only; other checked rows stay selected.
   */
  toggleCheckboxSelection: (id: string) => void;
}

/**
 * Builds and maintains expandable order-auth table rows from clubbed source data.
 *
 * Hydrates checkbox selection from data-accumulator context and writes changes
 * back so linked-order modals can reopen with the same selection. Expansion is
 * tracked separately so multi-select does not open every checked row.
 *
 * @param sourceData - Order/auth pairs used to generate table rows
 * @param onSelectedOrderIdsChange - Optional callback when checkbox selection changes
 * @returns Table rows plus expansion and checkbox handlers
 */
export const useExpandableOrderAuthTable = (
  sourceData: SingleOrderClubbedData[],
  onSelectedOrderIdsChange?: (orderIds: string[]) => void,
): UseExpandableOrderAuthTableResult => {
  const {
    selectedSiblingOrderIds,
    setSelectedSiblingOrderIds,
    isSiblingOrderSelectionDisabled,
  } = useDataAccumulatorForSingleOrder();
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const [tableData, setTableData] = useState<
    ExpandableTableForOrderAuthTableRow[]
  >([]);
  /** Only the last checkbox-selected (or chevron-opened) row stays expanded. */
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  useEffect(() => {
    setTableData(
      generateExpandableTableForOrderAuthData(
        sourceData,
        authStatusOptions ?? [],
        {
          selectedOrderIds: selectedSiblingOrderIds,
          expandedOrderId,
          isSelectionDisabled: isSiblingOrderSelectionDisabled,
        },
      ),
    );
  }, [
    authStatusOptions,
    expandedOrderId,
    isSiblingOrderSelectionDisabled,
    selectedSiblingOrderIds,
    sourceData,
  ]);

  useEffect(() => {
    onSelectedOrderIdsChange?.(selectedSiblingOrderIds);
  }, [onSelectedOrderIdsChange, selectedSiblingOrderIds]);

  /**
   * Updates which single row is expanded from the chevron control.
   *
   * @param expanded - Whether the requested row should be expanded
   * @param id - Chevron identifier for the requested row
   */
  const handleRowExpansionChange = useCallback(
    (expanded: boolean, id: string) => {
      setExpandedOrderId(expanded ? id : null);
    },
    [],
  );

  /**
   * Toggles a row in/out of multi-select. Newly checked rows auto-expand;
   * previously checked rows remain selected but collapse.
   *
   * @param id - Row order id tied to the checkbox cell
   */
  const toggleCheckboxSelection = useCallback(
    (id: string) => {
      if (isSiblingOrderSelectionDisabled) {
        return;
      }

      const isCurrentlySelected = selectedSiblingOrderIds.includes(id);

      if (isCurrentlySelected) {
        setSelectedSiblingOrderIds(
          selectedSiblingOrderIds.filter((orderId) => orderId !== id),
        );
        setExpandedOrderId((currentExpandedId) =>
          currentExpandedId === id ? null : currentExpandedId,
        );
        return;
      }

      setSelectedSiblingOrderIds([...selectedSiblingOrderIds, id]);
      setExpandedOrderId(id);
    },
    [
      isSiblingOrderSelectionDisabled,
      selectedSiblingOrderIds,
      setSelectedSiblingOrderIds,
    ],
  );

  return {
    tableData,
    handleRowExpansionChange,
    toggleCheckboxSelection,
  };
};
